/**
 * @file        src/pages/enquiry/EnquiryList.jsx
 * @description Query Management → Query List. Read-only list of customer
 *              enquiries (contact form + package "Enquire now") persisted by
 *              the booking backend (GET /api/enquiries), with search, brand
 *              filter, read/unread filter, a mark-read/unread toggle and
 *              delete — no create/edit, enquiries are authored only by
 *              customers on the public site.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-08-04 Claude — Initial (CR A5: replaces the /queries Stub).
 *   - 2026-08-06 Claude — Message column now truncates to 2 lines with a
 *                          "View" row action opening EnquiryDetailModal for
 *                          the full, untruncated enquiry — previously the
 *                          only way to read a long message was the cramped
 *                          280px inline cell.
 *   - 2026-08-10 Claude — Status filter now uses the themed Select
 *                          (console-wide dropdown consistency pass) instead of
 *                          a native <select>.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import BrandSelect from '../../components/BrandSelect';
import Select from '../../components/Select';
import EnquiryDetailModal from './EnquiryDetailModal';
import { get, patch, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

const READ_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'false', label: 'Unread' },
  { value: 'true', label: 'Read' },
];

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function EnquiryList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [brand, setBrand] = useState('');
  const [isRead, setIsRead] = useState('');
  const [viewing, setViewing] = useState(null);

  const qc = useQueryClient();
  const params = { page, pageSize };
  if (applied) params.q = applied;
  if (brand) params.brand = brand;
  if (isRead) params.isRead = isRead;

  const { data, isLoading } = useQuery({
    queryKey: qk.enquiries.list(params),
    queryFn: () => get('/enquiries', { params }),
    placeholderData: keepPreviousData,
  });

  const readMut = useMutation({
    mutationFn: ({ id, is_read }) => patch(`/enquiries/${id}`, { is_read }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['enquiries'] }),
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not update'),
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/enquiries/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Enquiry removed.');
      qc.invalidateQueries({ queryKey: ['enquiries'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  async function onDelete(row) {
    if (!(await confirm(`Delete the enquiry from "${row.name}"?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead title="Query List" subtitle="Query Management · contact & package enquiries" />

      <article className="panel glass">
        <form
          className="search-row"
          onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}
        >
          <div className="field">
            <label>Name / Email / Phone / Message</label>
            <input placeholder="Search enquiries…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Brand</label>
            <BrandSelect value={brand} onChange={(v) => { setPage(1); setBrand(v); }} withAll />
          </div>
          <div className="field">
            <label>Status</label>
            <Select value={isRead} onChange={(v) => { setPage(1); setIsRead(v); }} options={READ_OPTIONS} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Email</th><th>Phone</th><th>Source</th>
                  <th>Message</th><th>Brand</th><th>Received</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={10} className="empty">No enquiries yet.</td></tr>
                ) : rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{r.name}</b></td>
                    <td>{r.email}</td>
                    <td className="mono">{r.phone || '—'}</td>
                    <td>{r.source === 'package' ? (r.package_name || 'Package') : 'Contact'}</td>
                    <td
                      style={{
                        maxWidth: 280, whiteSpace: 'pre-wrap', overflow: 'hidden',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {r.message}
                    </td>
                    <td className="mono">{(r.brand || '—').toUpperCase()}</td>
                    <td>{fmtDate(r.created_at)}</td>
                    <td><Chip tone={r.is_read ? 'ok' : 'warn'}>{r.is_read ? 'Read' : 'Unread'}</Chip></td>
                    <td className="row-act">
                      <button className="ib" aria-label="View" title="View" onClick={() => setViewing(r)}>
                        <Icon name="eye" />
                      </button>
                      <button
                        className="ib edit"
                        aria-label={r.is_read ? 'Mark unread' : 'Mark read'}
                        onClick={() => readMut.mutate({ id: r.id, is_read: !r.is_read })}
                      >
                        <Icon name={r.is_read ? 'x' : 'check'} />
                      </button>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(r)}>
                        <Icon name="trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {viewing ? <EnquiryDetailModal enquiry={viewing} onClose={() => setViewing(null)} /> : null}
    </>
  );
}
